import { subscriptionClient } from '@/helpers/api/apiClients'
import { CancelSubscriptionRequest, UpgradeSubscriptionRequest } from '@/helpers/api/WebApiClient'
import { createAuthenticatedFetch } from '@/helpers/api/httpClient'
import config from '@/config'
import type { TenantSubscriptionDetailResponse } from './subscriptionTenantTypes'
import { toPaymentKindQuery, type TransactionDetailResponse } from './transactionTypes'

const apiFetch = createAuthenticatedFetch()
const paymentsBase = () => `${config.API_URL}/api/subscription`

async function parseJson<T>(res: Response): Promise<T> {
	if (!res.ok) {
		const err = await res.json().catch(() => ({}))
		return Promise.reject(err)
	}
	return res.json() as Promise<T>
}

export const subscriptionTenantApi = {
	// The generated client returns Moment objects for date fields (expiredOn, paymentPending,
	// invoice periods); the rest of the app works with plain ISO strings, so normalize here.
	getCurrentSubscriptionDetail: async (): Promise<TenantSubscriptionDetailResponse> => {
		const r = await subscriptionClient.getCurrentSubscriptionDetail()
		return {
			name: r.name,
			price: r.price,
			startedOn: r.startedOn ? r.startedOn.toISOString() : undefined,
			expiredOn: r.expiredOn ? r.expiredOn.toISOString() : '',
			paymentPending: r.paymentPending ? r.paymentPending.toISOString() : undefined,
			isOverDue: r.isOverDue,
			fkSubscriptionPlanPKId: r.fkSubscriptionPlanPKId,
			latePaymentOverDue: r.latePaymentOverDue,
			invoices: r.invoices?.map((inv) => ({
				invoiceId: inv.invoiceId,
				periodFrom: inv.periodFrom ? inv.periodFrom.toISOString() : undefined,
				periodTo: inv.periodTo ? inv.periodTo.toISOString() : undefined,
				invoiceStatus: inv.invoiceStatus,
				stripeInvoiceUrl: inv.stripeInvoiceUrl,
			})),
			subscriptionStatus: r.subscriptionStatus,
			isTrial: r.isTrial,
			licenseKey: r.licenseKey,
			trialDaysRemaining: r.trialDaysRemaining,
			maxUsers: r.maxUsers,
			activeUserCount: r.activeUserCount,
			maxInstances: r.maxInstances,
			maxDatabases: (r as any).maxDatabases,
			activeDatabaseCount: (r as any).activeDatabaseCount,
			supportType: r.supportType,
			licenseStatus: r.licenseStatus,
			canUseProduct: r.canUseProduct,
			pendingLicenseIssue: r.pendingLicenseIssue,
			enforcementMode: r.enforcementMode,
			canUpgrade: r.canUpgrade,
			hasUpcomingSubscription: r.hasUpcomingSubscription,
			daysUntilExpiry: r.daysUntilExpiry,
			canRenew: r.canRenew,
			isExpired: r.isExpired,
			isSuperAdminTenant: r.isSuperAdminTenant,
			hasNoSubscription: r.hasNoSubscription,
			availablePacks: ((r as any).availablePacks ?? []).map((pack: any) => ({
				id: pack.id,
				name: pack.name,
				userCount: pack.userCount,
				userPrice: pack.userPrice,
				instanceCount: pack.instanceCount,
				instancePrice: pack.instancePrice,
				databaseCount: pack.databaseCount ?? 0,
				databasePrice: pack.databasePrice ?? 0,
				totalPrice: pack.totalPrice,
				isActive: pack.isActive,
				displayOrder: pack.displayOrder,
			})),
			maxLoginUsers: r.maxLoginUsers,
			needsLoginSeatSelection: r.needsLoginSeatSelection,
		}
	},

	getPublicPlans: () =>
		subscriptionClient.getPublicPlans(),

	/** Anonymous — for the marketing / pricing page (no auth required). */
	browsePlans: () =>
		subscriptionClient.browsePlans(),

	cancelSubscription: (cancellationReason?: string) =>
		subscriptionClient.cancelSubscription(new CancelSubscriptionRequest({ cancellationReason })),

	getPayments: async () => {
		const res = await apiFetch(`${paymentsBase()}/subscription/payments`)
		return parseJson<Array<{
			id: number
			kind?: string | number
			planName: string
			amount: number
			currency: string
			paymentStatus: string | number
			stripePaymentIntentId?: string
			invoiceUrl?: string
			createdOn: string
		}>>(res)
	},

	getPaymentDetail: async (id: number, kind: string | number | undefined) => {
		const res = await apiFetch(`${paymentsBase()}/subscription/payments/${id}?kind=${toPaymentKindQuery(kind)}`)
		return parseJson<TransactionDetailResponse>(res)
	},

	getLicense: () =>
		subscriptionClient.getLicense(),

	renew: (subscriptionPlanId: number) =>
		subscriptionClient.renewSubscription(subscriptionPlanId),

	upgrade: (targetSubscriptionPlanId: number) =>
		subscriptionClient.upgradeSubscription(new UpgradeSubscriptionRequest({ targetSubscriptionPlanId })),

	claimFree: async (subscriptionPlanId: number) => {
		const res = await apiFetch(`${paymentsBase()}/subscription/claim-free`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ subscriptionPlanId }),
		})
		return parseJson<{ id: number; message?: string }>(res)
	},
}

// Keep re-exporting the response type so callers don't need to change imports
export type { AssignSubscriptionPlanResponse } from '@/helpers/api/WebApiClient'
